/**
 * Footer.jsx — Site Footer
 *
 * A minimal footer bar always rendered at the bottom of every page/step.
 * Shows the SVG logo on the left, a linked Instagram handle with icon in
 * the centre, and the current copyright year on the right.
 */
import { useLocation, Link } from 'react-router-dom';
import ViriLogo from './icons/ViriLogo';

/** Instagram glyph — rounded square + circle + dot (Feather/Lucide style) */
function InstagramIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Footer() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  return (
    <footer
      className="footer-bar"
      style={{
        padding: '28px 60px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #DDD8CC',
        background: isAdmin ? '#FCF7F1' : '#F9F2E9',
      }}
    >
      <div className="footer-logo" style={{ marginLeft: '-28px' }}>
        <ViriLogo
          size={100}
          showWordmark={false}
          strokeColor="#1a1208"
          strokeWidth={7}
        />
      </div>

      {/* Instagram link — yellow pill button */}
      <a
        href="https://www.instagram.com/virisclayworks/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 9,
          background: '#E8A020',
          color: '#fff',
          textDecoration: 'none',
          borderRadius: 50,
          padding: '10px 20px',
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: '0.85rem',
          letterSpacing: '0.1em',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          boxShadow: '0 2px 8px rgba(232,160,32,0.25)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(232,160,32,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(232,160,32,0.25)';
        }}
      >
        <InstagramIcon size={18} />
        @virisclayworks
      </a>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
        }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <Link
            to="/privacy"
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: '0.7rem',
              color: '#9C7A3C',
              textDecoration: 'none',
              letterSpacing: '0.05em',
            }}
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: '0.7rem',
              color: '#9C7A3C',
              textDecoration: 'none',
              letterSpacing: '0.05em',
            }}
          >
            Terms
          </Link>
        </div>
        <span className="caption footer-copyright">
          © {new Date().getFullYear()}
        </span>
      </div>
    </footer>
  );
}
